import { Link } from 'react-router-dom';
import { Icon } from '../ui/Icon';
import { classeBouton } from '../ui/kit';
import { BOUTIQUES, REGLAGES, VALEURS } from '../data/mock-data';
import { familles } from '../data/repository';

/**
 * PIED DE PAGE — informatif, calme, et replié sur téléphone.
 *
 * L'ancien pied empilait quatre colonnes de liens sur mobile : illisible et
 * impossible à parcourir au pouce. Ici, chaque colonne devient un accordéon natif
 * (`<details>`), et l'essentiel reste visible : les trois adresses, le téléphone,
 * les horaires du jour.
 */
export function Footer() {
  return (
    <footer className="mt-24 bg-ink text-lin-50">
      {/* Bandeau de réassurance */}
      <div className="border-b border-lin-50/10">
        <div className="mx-auto grid max-w-[1280px] gap-6 px-5 py-10 sm:grid-cols-2 sm:px-6 lg:grid-cols-4">
          {[
            { icone: 'knife' as const, titre: 'Découpé à la commande', texte: 'Chaque pièce est préparée le matin même, selon vos préférences.' },
            { icone: 'snow' as const, titre: 'Chaîne du froid tenue', texte: `Colis ${REGLAGES.livraison.temperature} ou retrait au comptoir réfrigéré.` },
            { icone: 'scale' as const, titre: 'Pesée transparente', texte: `Prix estimé, poids réel. Au-delà de ±${REGLAGES.poids.tolerancePct} %, la maison absorbe.` },
            { icone: 'store' as const, titre: 'Trois boutiques', texte: 'Sannois, Franconville, Argenteuil — et un atelier d’abattage.' },
          ].map(item => (
            <div key={item.titre} className="flex gap-3.5">
              <span className="mt-0.5 grid h-10 w-10 shrink-0 place-items-center rounded-full bg-lin-50/10 text-brass-soft">
                <Icon name={item.icone} size={19} />
              </span>
              <div>
                <p className="text-sm font-semibold text-lin-50">{item.titre}</p>
                <p className="mt-1 text-[0.8125rem] leading-relaxed text-lin-50/65">{item.texte}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="mx-auto max-w-[1280px] px-5 py-12 sm:px-6">
        {/* Appel au comptoir */}
        <div className="mb-12 flex flex-col gap-5 rounded-[24px] bg-lin-50/[0.06] p-6 sm:flex-row sm:items-center sm:justify-between sm:p-8">
          <div>
            <p className="eyebrow text-brass-soft">Le comptoir répond</p>
            <p className="mt-2 font-display text-2xl text-lin-50 sm:text-3xl">
              Un doute sur une pièce, une quantité, une cuisson ?
            </p>
            <p className="mt-2 text-sm text-lin-50/65">
              Du mardi au samedi, 7h30 – 19h30. C’est un boucher qui décroche, pas un serveur vocal.
            </p>
          </div>
          <a href={`tel:${REGLAGES.marque.telephone.replace(/\s/g, '')}`}
            className={classeBouton('primary', 'btn--lg shrink-0')}>
            <Icon name="phone" size={18} /> {REGLAGES.marque.telephone}
          </a>
        </div>

        <div className="grid gap-x-8 gap-y-10 sm:grid-cols-2 lg:grid-cols-[1.3fr_1fr_1fr_1.2fr]">
          {/* Marque */}
          <div>
            <p className="font-display text-2xl text-lin-50">Maison Duval</p>
            <p className="mt-1 text-[0.625rem] font-semibold uppercase tracking-[0.24em] text-brass-soft">
              {REGLAGES.marque.baseline} · depuis {REGLAGES.marque.depuis}
            </p>
            <p className="mt-4 max-w-xs text-sm leading-relaxed text-lin-50/65">
              Une boucherie de famille du Val-d’Oise : trois boutiques, un atelier d’abattage,
              et la même idée depuis 1922 — on ne vend que ce qu’on a choisi.
            </p>
            <ul className="mt-5 space-y-1.5">
              {VALEURS.map(v => (
                <li key={v.titre} className="flex items-center gap-2 text-[0.8125rem] text-lin-50/70">
                  <Icon name="check" size={14} className="text-brass-soft" /> {v.titre}
                </li>
              ))}
            </ul>
          </div>

          {/* Colonnes : accordéons sur mobile, listes sur desktop */}
          <ColonneFooter titre="Le comptoir" liens={familles().slice(0, 6).map(f => ({ libelle: f.nom, vers: `/boutique/${f.slug}` }))} />
          <ColonneFooter titre="La maison" liens={[
            { libelle: 'Notre histoire', vers: '/notre-maison' },
            { libelle: 'Nos boutiques', vers: '/nos-boutiques' },
            { libelle: 'Livraison & retrait', vers: '/livraison-et-retrait' },
            { libelle: 'Nos engagements', vers: '/notre-maison#engagements' },
          ]} />

          {/* Boutiques */}
          <div>
            <p className="mb-4 hidden text-sm font-semibold text-lin-50 sm:block">Nous trouver</p>
            <details className="group sm:hidden" open>
              <summary className="mb-3 flex cursor-pointer items-center justify-between text-sm font-semibold [&::-webkit-details-marker]:hidden">
                Nous trouver <Icon name="chevronDown" size={16} className="transition group-open:rotate-180" />
              </summary>
              <ListeBoutiques />
            </details>
            <div className="hidden sm:block"><ListeBoutiques /></div>
          </div>
        </div>

        <div className="mt-12 flex flex-col gap-4 border-t border-lin-50/10 pt-6 text-[0.75rem] text-lin-50/50 sm:flex-row sm:items-center sm:justify-between">
          <p>© {new Date().getFullYear()} Maison Duval — Boucherie, charcuterie, volailles. Sannois · Franconville · Argenteuil.</p>
          <ul className="flex flex-wrap gap-x-5 gap-y-2">
            {[
              { t: 'Mentions légales', vers: '/mentions-legales#mentions' },
              { t: 'CGV & arrhes', vers: '/mentions-legales#cgv' },
              { t: 'Confidentialité', vers: '/mentions-legales#rgpd' },
              { t: 'Cookies', vers: '/mentions-legales#cookies' },
              { t: 'Accessibilité', vers: '/mentions-legales#accessibilite' },
            ].map(l => (
              <li key={l.t}><Link to={l.vers} className="transition hover:text-lin-50">{l.t}</Link></li>
            ))}
          </ul>
        </div>
        <p className="mt-4 text-[0.6875rem] text-lin-50/55">
          Vitrine de démonstration — les visuels sont des photographies d’illustration, les prix et disponibilités sont simulés.
        </p>
      </div>
    </footer>
  );
}

function ColonneFooter({ titre, liens }: { titre: string; liens: Array<{ libelle: string; vers: string }> }) {
  return (
    <div>
      <p className="mb-4 hidden text-sm font-semibold text-lin-50 sm:block">{titre}</p>
      <details className="group sm:hidden">
        <summary className="mb-3 flex cursor-pointer items-center justify-between text-sm font-semibold [&::-webkit-details-marker]:hidden">
          {titre} <Icon name="chevronDown" size={16} className="transition group-open:rotate-180" />
        </summary>
        <ul className="space-y-2.5 pb-2">
          {liens.map(l => (
            <li key={l.vers}>
              <Link to={l.vers} className="text-sm text-lin-50/65 transition hover:text-lin-50">{l.libelle}</Link>
            </li>
          ))}
        </ul>
      </details>
      <ul className="hidden space-y-2.5 sm:block">
        {liens.map(l => (
          <li key={l.vers}>
            <Link to={l.vers} className="text-sm text-lin-50/65 transition hover:text-lin-50">{l.libelle}</Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

function ListeBoutiques() {
  return (
    <ul className="space-y-4">
      {BOUTIQUES.map(b => (
        <li key={b.id} className="text-sm">
          <p className="font-medium text-lin-50">{b.nom}</p>
          <p className="text-lin-50/55">{b.adresse}</p>
          <a href={`tel:${b.telephone.replace(/\s/g, '')}`} className="mt-0.5 inline-flex items-center gap-1.5 text-[0.8125rem] text-brass-soft hover:underline">
            <Icon name="phone" size={12} /> {b.telephone}
          </a>
        </li>
      ))}
    </ul>
  );
}
